import { useState, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import {
  components as selectComponents,
  ClearIndicatorProps,
} from 'react-select'
import { connect } from 'scrivito'

import { ClientOption, FilterValues } from '../../types'
import { Button } from '@/Atomic/Molecules/Button'
import { Select } from '@/Atomic/Molecules/Inputs/Select'
import { FoldingBox } from '@/Components/FoldingBox/FoldingBox'

interface OrderFilterDropdownProps {
  initialFilters?: FilterValues
  customerOptions: ClientOption[]
  onFilterChange: (filters: FilterValues) => void
}

const getClearableComponents = (onClear: () => void) => ({
  IndicatorSeparator: () => null,
  ClearIndicator: (props: ClearIndicatorProps) => (
    <selectComponents.ClearIndicator
      {...props}
      innerProps={{
        ...props.innerProps,
        onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation()
          e.preventDefault()
          onClear()
          props.innerProps?.onMouseDown?.(
            e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>,
          )
        },
      }}
    />
  ),
})

export const OrderFilterDropdown = connect(
  ({
    initialFilters,
    customerOptions,
    onFilterChange,
  }: OrderFilterDropdownProps) => {
    const { t } = useTranslation()

    const getInitialState = (): string | null => {
      return initialFilters?.customer || null
    }

    const [isOpen, setIsOpen] = useState(false)
    const [selectedCustomer, setSelectedCustomer] = useState<string | null>(
      getInitialState,
    )
    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (!isOpen) return

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          setIsOpen(false)
        }
      }

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement
        const isSelectMenu = target.closest('.basic-select__menu')

        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(target) &&
          !isSelectMenu
        ) {
          setIsOpen(false)
        }
      }

      document.addEventListener('keydown', handleEscape)
      document.addEventListener('mousedown', handleClickOutside)

      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isOpen])

    const handleCustomerChange = (value: unknown) => {
      setSelectedCustomer((value as string) || null)
    }

    const handleApply = () => {
      const filterValues: FilterValues = {
        status: null,
        team: null,
        customer: selectedCustomer || null,
        order: null,
        workPackage: null,
        showWithRevenue: initialFilters?.showWithRevenue,
      }
      onFilterChange(filterValues)
      setIsOpen(false)
    }

    const handleClear = () => {
      setSelectedCustomer(null)
      onFilterChange({
        status: null,
        team: null,
        customer: null,
        order: null,
        workPackage: null,
        showWithRevenue: initialFilters?.showWithRevenue,
      })
    }

    const activeFilterCount = selectedCustomer ? 1 : 0

    const filterBody = (
      <div className="time-tracking-filter-content">
        <h5 className="time-tracking-filter-title">
          {t('timeTracking.filters')}
        </h5>

        <div className="time-tracking-filter-section">
          <label className="form-label">
            {t('timeTracking.filterLabels.customer')}
          </label>
          <Select
            key={`customer-${selectedCustomer || 'empty'}`}
            name=""
            options={customerOptions}
            defaultValue={selectedCustomer}
            onChange={(value) => handleCustomerChange(value)}
            placeholder={t('timeTracking.filterPlaceholders.customer')}
            components={getClearableComponents(() =>
              handleCustomerChange(null),
            )}
          />
        </div>

        <div className="time-tracking-filter-footer">
          <Button
            variant="outlined"
            title={t('timeTracking.clearFilters')}
            size="sm"
            onClick={handleClear}
          />
          <Button
            variant="primary"
            title={t('timeTracking.applyFilters')}
            size="sm"
            onClick={handleApply}
          />
        </div>
      </div>
    )

    return (
      <div className="time-tracking-filter-wrapper" ref={wrapperRef}>
        <Button
          variant="outlined"
          title={
            activeFilterCount > 0
              ? `${t('timeTracking.filter')} (${activeFilterCount})`
              : t('timeTracking.filter')
          }
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          icon="chevron-down"
        />

        <FoldingBox
          isBoxOpen={isOpen}
          close={() => setIsOpen(false)}
          body={filterBody}
        />
      </div>
    )
  },
)
